import demoLeagueData, { roster, weekLabels, weekOptions } from "./demoLeagueData";

const DEMO_PROVIDER = "demo";
const DEMO_LEAGUE_PK = "demo-league-pk";
const DEMO_LEAGUE_ID = "demo-2025-alpha";
const DEMO_YEAR = "2025";

const clone = (value) => JSON.parse(JSON.stringify(value));

const jsonResponse = (payload, init = {}) => ({
    ok: init.ok ?? true,
    status: init.status ?? 200,
    statusText: init.statusText ?? "OK",
    headers: {
        get: (name) => {
            const headers = init.headers || {};
            return headers[name] || headers[name?.toLowerCase?.()] || null;
        },
    },
    json: async () => clone(payload),
    blob: async () =>
        new Blob([typeof payload === "string" ? payload : JSON.stringify(payload)], {
            type: init.contentType || "application/json",
        }),
});

const demoLeague = () => ({
    id: DEMO_LEAGUE_PK,
    league_name: demoLeagueData.league.name,
    league_year: demoLeagueData.league.season,
    league_id: demoLeagueData.league.leagueId,
    provider: DEMO_PROVIDER,
    is_owner: true,
    discord_webhook_url: demoLeagueData.settings.discord.webhook,
    data_profile: demoLeagueData.league.leagueType,
    has_league_cup: true,
    capabilities: {
        data_profile: demoLeagueData.league.leagueType,
        supports_ai_report: true,
        supports_league_cup: true,
        supports_naughty_list: true,
        supports_player_stats: true,
        supports_position_records: true,
        supports_roster_values: true,
        supports_simulation: true,
    },
});

const demoSettings = () => ({
    n_teams: roster.length,
    n_playoff_spots: demoLeagueData.simulation.playoffTeams,
    n_regular_season_weeks: 14,
    regular_season_complete: false,
    playoff_week_start: 15,
    playoff_matchup_period_length: 1,
    playoff_seeding_tiebreaker: "points_for",
});

const bootstrap = () => ({
    ...demoLeague(),
    current_week: demoLeagueData.league.currentWeek + 1,
    n_completed_weeks: demoLeagueData.league.currentWeek,
    completed_weeks: weekOptions,
    week_labels: weekLabels,
    is_custom_scoring: false,
});

const snapshotForWeek = (week) =>
    demoLeagueData.weeklySnapshots[week] ||
    demoLeagueData.weeklySnapshots[demoLeagueData.league.currentWeek] ||
    demoLeagueData.weeklySnapshots[weekOptions[weekOptions.length - 1]];

const recordsPayload = () => ({
    league_name: demoLeagueData.league.name,
    league_id: demoLeagueData.league.leagueId,
    capabilities: demoLeague().capabilities,
    best_team_stats: demoLeagueData.records.bestTeamStats,
    worst_team_stats: demoLeagueData.records.worstTeamStats,
    best_position_stats: demoLeagueData.records.bestPositionalStats,
    worst_position_stats: demoLeagueData.records.worstPositionalStats,
});

const weeklyRecordsPayload = (week) => ({
    league_records: demoLeagueData.weeklyRecords[week]?.league_records || [],
    franchise_records: demoLeagueData.weeklyRecords[week]?.franchise_records || [],
    other_signals: demoLeagueData.weeklyRecords[week]?.other_signals || [],
    owner_scope: "all",
    coverage: {
        years: [2023, 2024, 2025],
        percent: 100,
    },
});

const cupTeams = roster.map((team, index) => ({
    provider_team_id: `demo-team-${index + 1}`,
    team_name: team.team,
    owner_name: team.owner,
}));

const cupSettingsPayload = () => ({
    is_configured: true,
    editable: false,
    league: {
        id: DEMO_LEAGUE_PK,
        league_name: demoLeagueData.league.name,
        league_id: DEMO_LEAGUE_ID,
        provider: DEMO_PROVIDER,
        year: Number(DEMO_YEAR),
        current_week: demoLeagueData.league.currentWeek + 1,
        min_week: 1,
        reg_season_count: 14,
        is_season_complete: false,
        teams: cupTeams,
    },
    cup: {
        ...demoLeagueData.cup,
        config: {
            ...demoLeagueData.cup.config,
            group_stage_weeks: demoLeagueData.cup.schedule.group_stage_weeks.length,
            advance_per_group: 1,
            knockout_round_length_weeks: 1,
        },
    },
    assignments: {
        "Group A": cupTeams.slice(0, 4),
        "Group B": cupTeams.slice(4),
    },
});

const cupOverviewPayload = () => ({
    ...demoLeagueData.cup,
    is_configured: true,
    permissions: { can_edit: true },
    week_labels: weekLabels,
});

const simulationPayload = () => ({
    playoff_odds: demoLeagueData.simulation.playoffOdds,
    rank_distribution: demoLeagueData.simulation.rankDistribution,
    seeding_outcomes: demoLeagueData.simulation.seedingOutcomes,
});

const remainingStrengthPayload = () => ({
    min_week: demoLeagueData.league.currentWeek + 1,
    max_week: 14,
    remaining_strength_of_schedule: roster.map((team, index) => ({
        team: team.team,
        owner: team.owner,
        opp_points_for: 1310 + index * 24.17,
        opp_win_pct: 0.61 - index * 0.018,
        opp_power_rank: 2.2 + index * 0.7,
        overall_difficulty: 91.5 - index * 4.6,
    })),
});

const isoDate = (year, month, day) =>
    new Date(Date.UTC(year, month - 1, day, 16, 30, 0)).toISOString();

const historyCoveragePayload = () => ({
    league_group_id: "demo-history-group",
    provider: DEMO_PROVIDER,
    canonical_id: DEMO_LEAGUE_ID,
    league_name: demoLeagueData.league.name,
    can_refresh: true,
    refreshable_years: [2023, 2024, 2025],
    linked_years: [2023, 2024, 2025],
    linked_members: [
        {
            user_id: "demo-user",
            username: "Demo Commissioner",
            email: "testuser@doritostats.com",
            is_requesting_user: true,
        },
    ],
    linked_seasons: [2025, 2024, 2023].map((year) => ({
        provider: DEMO_PROVIDER,
        year,
        league_id: DEMO_LEAGUE_ID,
        league_name: demoLeagueData.league.name,
        can_refresh: true,
    })),
    season_coverage: [
        {
            year: 2025,
            is_linked: true,
            row_count: 48,
            completed_week_count: 12,
            completed_weeks: Array.from({ length: 12 }, (_, i) => i + 1),
            last_week: 12,
            can_refresh: true,
            latest_row_fetched_at: isoDate(2026, 6, 20),
            data_refresh: {
                is_season_complete: false,
                last_fetched_at: isoDate(2026, 6, 20),
            },
        },
        {
            year: 2024,
            is_linked: true,
            row_count: 56,
            completed_week_count: 14,
            completed_weeks: Array.from({ length: 14 }, (_, i) => i + 1),
            last_week: 14,
            can_refresh: true,
            latest_row_fetched_at: isoDate(2026, 6, 18),
            data_refresh: {
                is_season_complete: true,
                last_fetched_at: isoDate(2026, 6, 18),
            },
        },
        {
            year: 2023,
            is_linked: true,
            row_count: 52,
            completed_week_count: 13,
            completed_weeks: Array.from({ length: 13 }, (_, i) => i + 1),
            last_week: 13,
            can_refresh: true,
            latest_row_fetched_at: isoDate(2026, 6, 17),
            data_refresh: {
                is_season_complete: true,
                last_fetched_at: isoDate(2026, 6, 17),
            },
        },
    ],
});

const holder = (ownerIndex, year, week, opponentIndex = null) => ({
    team_owner: roster[ownerIndex].owner,
    team_name: roster[ownerIndex].team,
    year,
    week,
    opponent_owner: opponentIndex === null ? null : roster[opponentIndex].owner,
});

const historyRecordsPayload = () => {
    const records = {
        highest_single_game_score: {
            key: "highest_single_game_score",
            label: "Highest single-game score",
            value: 181.09,
            value_format: "number",
            active_holder: holder(0, 2025, 12, 3),
        },
        best_average_points_season: {
            key: "best_average_points_season",
            label: "Best average points season",
            value: 154.3,
            value_format: "number",
            active_holder: holder(0, 2025),
        },
        longest_win_streak: {
            key: "longest_win_streak",
            label: "Longest win streak",
            value: 7,
            value_format: "integer",
            value_units: "wins",
            active_holder: holder(1, 2024),
        },
        lowest_single_game_score: {
            key: "lowest_single_game_score",
            label: "Lowest single-game score",
            value: 72.4,
            value_format: "number",
            active_holder: holder(7, 2023, 8, 4),
        },
        worst_average_points_season: {
            key: "worst_average_points_season",
            label: "Worst average points season",
            value: 101.2,
            value_format: "number",
            active_holder: holder(7, 2023),
        },
        longest_loss_streak: {
            key: "longest_loss_streak",
            label: "Longest losing streak",
            value: 6,
            value_format: "integer",
            value_units: "losses",
            active_holder: holder(7, 2023),
        },
    };
    const recordOrder = Object.keys(records);
    return {
        league_name: demoLeagueData.league.name,
        years: [2023, 2024, 2025],
        latest_season_year: 2025,
        current_owner_count: roster.length,
        record_order: recordOrder,
        records,
        all_owner_record_order: recordOrder,
        all_owner_records: records,
        current_owner_record_order: recordOrder,
        current_owner_records: records,
    };
};

const leaderboardRow = (team, index) => {
    const wins = [58, 49, 45, 44, 41, 38, 34, 28][index];
    const losses = [26, 33, 37, 38, 39, 42, 45, 52][index];
    const games = wins + losses;
    const pointsFor = 12344.8 - index * 221.7;
    return {
        owner: team.owner,
        team_name: team.team,
        value: wins,
        games_played: games,
        seasons: [2023, 2024, 2025],
        is_active_owner: true,
        all_time_wins: wins,
        all_time_losses: losses,
        win_percentage: wins / games,
        points_for: pointsFor,
        points_per_game: pointsFor / games,
        points_against: 10811.3 + index * 119.4,
        weekly_high_scores: 9 - Math.min(index, 6),
        top_weekly_finishes: 21 - index,
        playoff_appearances: Math.max(1, 9 - index),
        playoff_wins: Math.max(0, 8 - index),
    };
};

const historyLeaderboardsPayload = () => {
    const rows = roster.map(leaderboardRow);
    const leaderboardFor = (key) =>
        rankRows(rows.map((row) => ({ ...row, value: row[key] ?? row.value })));
    return {
        league_name: demoLeagueData.league.name,
        years: [2023, 2024, 2025],
        latest_season_year: 2025,
        current_owner_count: roster.length,
        owners: roster.map((team) => ({
            owner_name: team.owner,
            seasons: [2023, 2024, 2025],
        })),
        leaderboards: {
            all_time_wins: leaderboardFor("all_time_wins"),
            all_time_losses: leaderboardFor("all_time_losses"),
            win_percentage: leaderboardFor("win_percentage"),
            points_for: leaderboardFor("points_for"),
            points_per_game: leaderboardFor("points_per_game"),
            points_against: leaderboardFor("points_against"),
            weekly_high_scores: leaderboardFor("weekly_high_scores"),
            top_weekly_finishes: leaderboardFor("top_weekly_finishes"),
            playoff_appearances: leaderboardFor("playoff_appearances"),
            playoff_wins: leaderboardFor("playoff_wins"),
        },
    };
};

const rankRows = (rows) =>
    [...rows]
        .sort((a, b) => Number(b.value || 0) - Number(a.value || 0))
        .map((row, index) => ({ ...row, rank: index + 1 }));

const ownerRecord = (ownerIndex, wins, losses, pointsFor, pointsAgainst) => ({
    owner: roster[ownerIndex].owner,
    wins,
    losses,
    ties: 0,
    win_percentage: wins / (wins + losses),
    points_for: pointsFor,
    points_against: pointsAgainst,
    playoff_wins: Math.max(0, Math.floor(wins / 3)),
    playoff_losses: Math.max(0, Math.floor(losses / 3)),
    playoff_ties: 0,
});

const rivalry = (id, name, indexes, summaryOverrides = {}) => ({
    id,
    name,
    custom_name: name,
    can_manage: true,
    member_count: indexes.length,
    members: indexes.map((index) => ({ owner_name: roster[index].owner })),
    summary: {
        total_matchups: summaryOverrides.total_matchups || 10,
        playoff_meetings: summaryOverrides.playoff_meetings || 1,
        owner_records:
            summaryOverrides.owner_records ||
            indexes.map((index, rowIndex) =>
                ownerRecord(index, 5 - rowIndex, 4 + rowIndex, 1510 - rowIndex * 55, 1480 + rowIndex * 33)
            ),
    },
    records: {
        highest_single_game_score: {
            value: summaryOverrides.high_score || 176.2,
            owner: roster[indexes[0]].owner,
            year: 2025,
            week: 12,
            is_playoff: false,
        },
    },
    head_to_head: {
        owners:
            summaryOverrides.owner_records ||
            indexes.map((index, rowIndex) =>
                ownerRecord(index, 5 - rowIndex, 4 + rowIndex, 1510 - rowIndex * 55, 1480 + rowIndex * 33)
            ),
        pairs: [],
    },
    recent_matchups: [
        {
            year: 2025,
            week: 12,
            is_playoff: false,
            teams: indexes.slice(0, 2).map((index, rowIndex) => ({
                owner: roster[index].owner,
                score: 181.09 - rowIndex * 21.87,
            })),
        },
        {
            year: 2024,
            week: 9,
            is_playoff: false,
            teams: indexes.slice(0, 2).map((index, rowIndex) => ({
                owner: roster[index].owner,
                score: 158.4 + rowIndex * 4.2,
            })),
        },
    ],
});

const historyRivalriesPayload = () => ({
    league_name: demoLeagueData.league.name,
    rivalry_count: 3,
    owner_options: roster.map((team) => ({
        owner_name: team.owner,
        seasons: [2023, 2024, 2025],
    })),
    rivalries: [
        rivalry(1, "Skyline Rivalry", [0, 4], { total_matchups: 10, high_score: 181.09 }),
        rivalry(2, "Boardroom Bowl", [1, 2], { total_matchups: 7, high_score: 169.44 }),
        rivalry(3, "Signal Derby", [5, 6], { total_matchups: 9, high_score: 164.83 }),
    ],
});

const ownerAliasesPayload = () => ({
    season_years: [2023, 2024, 2025],
    conflict_count: 0,
    owner_rows: roster.map((team, index) => ({
        raw_name: team.owner,
        alias_id: `demo-alias-${index + 1}`,
        canonical_name: team.owner,
        has_alias: true,
        seasons: [2023, 2024, 2025],
        team_names: [team.team],
        conflicts: [],
    })),
});

const mutationPayload = (pathname) => {
    if (pathname.endsWith("/share/")) {
        return {
            url: `${window.location.origin}/share/demo-static-preview`,
        };
    }
    if (pathname.endsWith("/discord-webhook/test/")) {
        return { success: true };
    }
    if (pathname.endsWith("/discord-webhook/")) {
        return { discord_webhook_url: demoLeagueData.settings.discord.webhook };
    }
    if (pathname.endsWith("/league-context/")) {
        return { league_context: demoLeagueData.settings.leagueContext };
    }
    if (pathname.includes("/cup/")) {
        return cupSettingsPayload();
    }
    if (pathname.includes("/owner-aliases/")) {
        return ownerAliasesPayload();
    }
    if (pathname.includes("/rivalries/")) {
        return historyRivalriesPayload().rivalries[0];
    }
    if (pathname.includes("/refresh")) {
        return {
            status: "complete",
            provider: DEMO_PROVIDER,
            year: 2025,
            week: null,
            rows_upserted: 0,
        };
    }
    return { ok: true };
};

const parseUrl = (url) => {
    try {
        const origin =
            typeof window !== "undefined" &&
            window.location?.origin &&
            window.location.origin !== "null"
                ? window.location.origin
                : "http://localhost";
        return new URL(url, origin);
    } catch {
        return null;
    }
};

const pathIncludesDemoProvider = (pathname) =>
    pathname.split("/").includes(DEMO_PROVIDER);

const isDemoLeaguePkPath = (pathname) =>
    pathname.includes(`/api/league/${DEMO_LEAGUE_PK}/`) ||
    pathname.endsWith(`/api/league/${DEMO_LEAGUE_PK}`);

export const getDemoFetchResponse = (url, options = {}) => {
    const parsed = parseUrl(url);
    if (!parsed) return null;

    const { pathname } = parsed;
    const method = (options?.method || "GET").toUpperCase();
    const isDemoProviderPath = pathIncludesDemoProvider(pathname);
    const isDemoPkPath = isDemoLeaguePkPath(pathname);

    if (!isDemoProviderPath && !isDemoPkPath) return null;

    if (method !== "GET") {
        return jsonResponse(mutationPayload(pathname));
    }

    const parts = pathname.split("/").filter(Boolean);
    const endpointNames = [
        "box-scores",
        "check-league-status",
        "historical",
        "ktc-roster-values",
        "league",
        "league-settings",
        "luck-index",
        "naughty-list",
        "position-breakdown",
        "position-cards",
        "power-rankings",
        "prefetch-season-data",
        "playoff-simulation",
        "remaining-strength-of-schedule",
        "season-records",
        "simulate-playoff-odds",
        "standings",
        "trade-hub",
        "weekly-awards",
        "weekly-records",
    ];
    const apiName = endpointNames.find((name) => parts.includes(name));
    const week = Number(parts[parts.length - 1]);

    if (isDemoPkPath) {
        if (pathname.endsWith("/league-context/")) {
            return jsonResponse({ league_context: demoLeagueData.settings.leagueContext });
        }
        if (pathname.endsWith("/owner-aliases/")) {
            return jsonResponse(ownerAliasesPayload());
        }
        if (pathname.endsWith("/cup/prior-seeding/")) {
            return jsonResponse({
                available: true,
                ranking: cupTeams.map((team, index) => ({
                    ...team,
                    rank: index + 1,
                    points_for: 1489.7 - index * 37.4,
                })),
            });
        }
        if (pathname.endsWith("/cup/")) {
            return jsonResponse(cupSettingsPayload());
        }
        return jsonResponse(mutationPayload(pathname));
    }

    if (apiName === "check-league-status") {
        return jsonResponse({ status: "ok" });
    }

    if (apiName === "league-settings") {
        return jsonResponse(demoSettings());
    }

    if (apiName === "prefetch-season-data") {
        return jsonResponse({
            total_weeks: demoLeagueData.league.currentWeek,
            cached_weeks: weekOptions,
        });
    }

    if (apiName === "season-records") {
        return jsonResponse(recordsPayload());
    }

    if (apiName === "trade-hub") {
        return jsonResponse({
            trades: [],
            owners: [],
            completed_weeks: demoLeagueData.league.currentWeek,
            required_weeks: [],
            cached_weeks: [],
            ready: true,
        });
    }

    if (apiName === "weekly-records") {
        return jsonResponse(weeklyRecordsPayload(week));
    }

    if (apiName === "simulate-playoff-odds") {
        return jsonResponse(simulationPayload());
    }

    if (apiName === "playoff-simulation") {
        const tab = parts[parts.indexOf("playoff-simulation") + 1];
        const fieldByTab = {
            odds: "playoff_odds",
            "finish-range": "rank_distribution",
            seeding: "seeding_outcomes",
        };
        return jsonResponse({
            data: simulationPayload()[fieldByTab[tab]],
            cached: true,
        });
    }

    if (apiName === "remaining-strength-of-schedule") {
        return jsonResponse(remainingStrengthPayload());
    }

    if (apiName === "historical") {
        if (pathname.endsWith("/coverage/")) return jsonResponse(historyCoveragePayload());
        if (pathname.endsWith("/records/")) return jsonResponse(historyRecordsPayload());
        if (pathname.endsWith("/leaderboards/")) return jsonResponse(historyLeaderboardsPayload());
        if (pathname.endsWith("/rivalries/")) return jsonResponse(historyRivalriesPayload());
        if (pathname.endsWith("/export/")) {
            return jsonResponse("year,week,home_owner,away_owner\n2025,12,Lyra North,Mateo Quill\n", {
                contentType: "text/csv",
                headers: {
                    "content-disposition": "attachment; filename=demo-history.csv",
                },
            });
        }
        return jsonResponse(mutationPayload(pathname));
    }

    if (apiName === "league") {
        if (pathname.endsWith("/bootstrap/")) return jsonResponse(bootstrap());
        if (pathname.endsWith("/current-week/")) {
            return jsonResponse({
                current_week: demoLeagueData.league.currentWeek + 1,
                n_completed_weeks: demoLeagueData.league.currentWeek,
            });
        }
        if (pathname.endsWith("/cup/")) return jsonResponse(cupOverviewPayload());
        return jsonResponse(demoLeague());
    }

    const snapshot = snapshotForWeek(week);
    const payloadByEndpoint = {
        "weekly-awards": snapshot.awards,
        standings: snapshot.standings,
        "box-scores": snapshot.boxScores,
        "power-rankings": snapshot.powerRankings,
        "luck-index": snapshot.luckIndex,
        "position-breakdown": snapshot.seasonMatrix,
        "position-cards": snapshot.teamCards,
        "naughty-list": snapshot.naughtyList,
        "ktc-roster-values": snapshot.ktcData,
    };

    if (payloadByEndpoint[apiName] !== undefined) {
        return jsonResponse(payloadByEndpoint[apiName]);
    }

    return jsonResponse({ error: "Demo endpoint not found" }, { ok: false, status: 404 });
};
